import * as ImagePicker from 'expo-image-picker';
import { useEffect, useRef, useState } from 'react';

import { assertUploadableSize, VideoTooLargeError } from '@/lib/voltrVideo';
import { voltrUploadStore, type UploadContext } from '@/stores/voltrUpload.store';
import { MAX_PICKS } from '@/stores/voltrUploadTypes';

export { MAX_PICKS };

/** The same words the uploader shows while another upload runs. */
const STILL_UPLOADING = 'Another upload is still going. Wait for it to finish.';

/**
 * The deal page's camera-roll picker: several videos at once, added to any
 * still waiting ("Add another video", up to MAX_PICKS in all), or one when
 * replacing a sent-back video (`replaces`, its id). The picks are stored with what they're for;
 * a failed pick leaves them as they were. The picker can stay open across a
 * logout, a Cancel or a back, so its answer is dropped when the page is gone
 * or the session changed.
 */
export function useVideoPicker(base: Omit<UploadContext, 'replaces'> | null) {
  const [pickError, setPickError] = useState<string | null>(null);
  const mounted = useRef(true);

  // Set on every mount, not just the first: React can run the effect's
  // cleanup and setup again (StrictMode, fast refresh) on the same instance.
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  async function pick(replaces?: string) {
    if (!base || voltrUploadStore.running) return;
    // Room left beside the picks already waiting for this deal (a replacement is one video).
    const waiting = !replaces && voltrUploadStore.isFor(base.actionId) && !voltrUploadStore.context?.replaces ? voltrUploadStore.items.length : 0;
    const room = MAX_PICKS - waiting;
    if (room <= 0) return;
    const context: UploadContext = replaces ? { ...base, replaces } : base;
    const generation = voltrUploadStore.generation;
    const stale = () => !mounted.current || voltrUploadStore.generation !== generation;
    setPickError(null);
    try {
      // A one-video limit on a multi-select picker misbehaves on some Androids: ask for one.
      const multi = !replaces && room > 1;
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ['videos'],
        allowsMultipleSelection: multi,
        ...(multi ? { selectionLimit: room } : {}),
      });
      if (stale() || result.canceled || result.assets.length === 0) return;
      result.assets.forEach((asset) => assertUploadableSize(asset.fileSize));
      const videos = result.assets.map((asset, i) => ({
        uri: asset.uri,
        fileName: asset.fileName ?? `video-${i + 1}.mp4`,
        mimeType: asset.mimeType ?? 'video/mp4',
        sizeBytes: asset.fileSize,
        file: asset.file,
      }));
      // The web picker ignores selectionLimit: keep what fits and say so.
      if (!voltrUploadStore.addVideos(videos.slice(0, room), context)) setPickError(STILL_UPLOADING);
      else if (videos.length > room) setPickError(`You can add up to ${MAX_PICKS} videos. Only the first ${room} were added.`);
    } catch (err) {
      if (stale()) return;
      setPickError(
        err instanceof VideoTooLargeError
          ? err.message
          : "Couldn't open your videos. Check that MarketPryce can see your photo library, then try again.",
      );
    }
  }

  return { pick, pickError };
}
